"use client";

import { useCallback, useRef, useState } from "react";
import { useAppSelector } from "@/redux/rootReducer";
import { selectAccessToken } from "@/redux/reducers/auth.reducer";
import { useSendMessageMutation } from "@/core/services/medconnect-agent-service";
import DocumentModel, { CreatePresignedUrlRequest, objectTypes } from "@/core/models/document.model";
import type { UploadedFileAttachment } from "@/core/models/medconnect-agent.model";
import { getEnv } from "@/utils/helpers";
import { appendUrlQueryParams } from "@/utils/url.utils";
import { utf8ToBase64 } from "./chatHistoryUtils";

export interface StartStreamHandlers {
    onHistory?: (chatHistory: any[]) => void;
    onDelta?: (token: string) => void;
    onToolCall?: (calls: Array<{ id: string; name: string; args?: any }>) => void;
    onToolEnd?: (sessionId?: string) => void;
    onComplete?: (sessionId?: string) => void;
}

export interface UseChatTransportArgs {
    currentSessionId?: string | null;
    updateSessionId: (id: string) => void;
    setError?: (msg: string | null) => void;
    fetchHistory?: (sessionId: string) => void; // optional: caller can supply their own
    t?: (key: string, vars?: any) => string; // i18n function (optional)
    platformMeta?: unknown;
    businessMeta?: unknown;
    agentType?: string;
    language?: string;
    /** LLM provider override (e.g., 'openai', 'bedrock') */
    model?: string;
}

export function useChatTransport(args: UseChatTransportArgs) {
    const { currentSessionId, updateSessionId, setError, fetchHistory, t, platformMeta, businessMeta, agentType, language, model } = args;
    const accessToken = useAppSelector(selectAccessToken);
    const [sendMessage] = useSendMessageMutation();

    const eventSourceRef = useRef<EventSource | null>(null);
    const [isStreaming, setIsStreaming] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const closeStream = useCallback(() => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
        setIsStreaming(false);
    }, []);

    const startStream = useCallback(
        (prompt: string, handlers?: StartStreamHandlers, sessionIdOverride?: string) => {
            setError?.(null);
            setIsStreaming(true);

            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }

            const sessionIdToUse = sessionIdOverride || currentSessionId || undefined;
            const sessionParam = sessionIdToUse ? `/${sessionIdToUse}` : "";
            const baseUrl = `${getEnv("VITE_API_URL") || "http://localhost:3000"}/medconnect-agent/chat/stream${sessionParam}`;

            // Build query parameters object
            const queryParams: Record<string, string> = {
                message: prompt,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            };

            // Add optional parameters if they exist
            if (accessToken) {
                queryParams.authorization = `Bearer ${encodeURIComponent(accessToken)}`;
            }
            if (platformMeta !== undefined) {
                queryParams.platformMeta = utf8ToBase64(JSON.stringify(platformMeta));
            }
            if (businessMeta !== undefined) {
                queryParams.businessMeta = utf8ToBase64(JSON.stringify(businessMeta));
            }
            if (agentType) {
                queryParams.agentType = agentType;
            }
            if (language) {
                queryParams.language = language;
            }
            if (model) {
                queryParams.model = model;
            }

            const streamUrl = appendUrlQueryParams(baseUrl, queryParams);

            const es = new EventSource(streamUrl);
            eventSourceRef.current = es;

            es.onmessage = (event) => {
                try {
                    const chatResponse = JSON.parse(event.data);

                    // Ignore heartbeat events (used to keep connection alive)
                    if (chatResponse.type === 'heartbeat') {
                        return;
                    }

                    if (!currentSessionId && chatResponse.sessionId) {
                        updateSessionId(chatResponse.sessionId);
                    }

                    if (chatResponse.chatHistory && Array.isArray(chatResponse.chatHistory)) {
                        handlers?.onHistory?.(chatResponse.chatHistory);
                    }

                    const isDelta: boolean | undefined = chatResponse.isDelta;
                    const token: string | undefined = chatResponse.token;
                    const toolEvent: "call" | "start" | "end" | undefined = chatResponse.toolEvent;
                    const toolCalls: Array<{ id: string; name: string; args?: any }> | undefined = chatResponse.toolCalls;

                    if (isDelta && typeof token === "string") {
                        handlers?.onDelta?.(token);
                    }

                    if (toolEvent === "call" && Array.isArray(toolCalls) && toolCalls.length > 0) {
                        handlers?.onToolCall?.(toolCalls);
                    }

                    if (toolEvent === "end") {
                        const sid = chatResponse.sessionId || currentSessionId || undefined;
                        // Do NOT call fetchHistory here — with 10 specialists × 5+ tools each,
                        // this triggers 50+ overlapping HTTP requests and setMessages floods.
                        // History is refreshed only on the completion event (where the server
                        // includes chatHistory in the SSE payload).
                        handlers?.onToolEnd?.(sid);
                    }

                    if ("complete" in chatResponse && chatResponse.complete) {
                        es.close();
                        eventSourceRef.current = null;
                        setIsStreaming(false);
                        handlers?.onComplete?.(chatResponse.sessionId || currentSessionId || undefined);
                    }
                } catch (error) {
                    console.error("[useChatTransport] SSE parsing error:", error);
                    setError?.(t ? t('page.medconnectAgent.chatInterface.errorParsingStream') : 'Stream parse error');
                    es.close();
                    eventSourceRef.current = null;
                    setIsStreaming(false);
                }
            };

            es.onerror = (error) => {
                console.error("[useChatTransport] Stream error:", error);
                setError?.(t ? t('page.medconnectAgent.chatInterface.streamConnectionError') : 'Stream connection error');
                es.close();
                eventSourceRef.current = null;
                setIsStreaming(false);
            };
        },
        [accessToken, currentSessionId, fetchHistory, setError, t, updateSessionId, platformMeta, businessMeta, agentType, language, model]
    );

    const createOrReuseSession = useCallback(async (): Promise<string> => {
        if (currentSessionId) return currentSessionId;
        const resp = await sendMessage({ messageContent: "", timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, agentType, language }).unwrap();
        const sid = resp.sessionId as string;
        if (sid) updateSessionId(sid);
        return sid;
    }, [currentSessionId, sendMessage, updateSessionId, agentType]);

    const uploadFile = useCallback(
        async (file: File, sid: string): Promise<UploadedFileAttachment | null> => {
            try {
                setIsUploading(true);
                const urlRequest: CreatePresignedUrlRequest = {
                    fileName: file.name,
                    fileType: file.type,
                    objectType: objectTypes.AGENT_CHAT_SESSION,
                    objectId: sid,
                    sessionId: sid,
                };
                const presigned = await DocumentModel.generatePresignedUrl(urlRequest);
                await DocumentModel.uploadDocumentToAwsS3(presigned.url, file);
                const documentDto = {
                    filename: presigned.fileName,
                    key: presigned.key,
                    objectType: objectTypes.AGENT_CHAT_SESSION,
                };
                const doc = await DocumentModel.addDocument(documentDto, objectTypes.AGENT_CHAT_SESSION, sid);
                return { filename: doc.filename, key: doc.key, url: doc.url || '', objectType: doc.objectType as string };
            } catch (error) {
                console.error('[useChatTransport] Error uploading file:', error);
                setError?.(t ? t('page.medconnectAgent.chatInterface.fileUploadFailed', { message: (error as Error).message }) : 'File upload failed');
                return null;
            } finally {
                setIsUploading(false);
            }
        },
        [setError, t]
    );

    const buildToolTagsForCalls = useCallback((calls: Array<{ id: string; name: string; args?: any }>) => {
        const tags = calls.map((tc) => {
            const interaction = { tool_call_id: tc.id, tool_name: tc.name, tool_args: tc.args || {}, result: undefined };
            return `[tool_interaction](${utf8ToBase64(JSON.stringify(interaction))})`;
        }).join('\n\n');
        return `\n\n${tags}\n\n`;
    }, []);

    return {
        isStreaming,
        isUploading,
        startStream,
        closeStream,
        createOrReuseSession,
        uploadFile,
        buildToolTagsForCalls,
    };
}

export default useChatTransport;

